import { logEncounterView } from '@backend/functions/gyn'
import { useApp } from '@hb/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useProfile } from '../../contexts/user/ProfileContext'
import {
  EncounterOpenMode,
  EncounterSurface,
  getCanEditEncounter,
  getEncounterSurface,
  getInitialOpenMode,
} from './encounterSurface'
import { getShouldLogEncounterView } from './encounterViewAudit'
import { EncounterSelection } from './types'

export type { EncounterOpenMode, EncounterSurface }
export { getCanEditEncounter, getEncounterSurface, getInitialOpenMode }

export interface EncounterOpenState {
  selection: EncounterSelection | null
  /** Surface the current selection renders on, or null when nothing is open. */
  surface: EncounterSurface | null
  /** Open (or close, with null) a selection — drop-in replacement for setOpenEncounter/setOpenExam. */
  open: (selection: EncounterSelection | null) => void
  /**
   * Pass to EncounterView's onEdit — switches view → form, capturing the amendment reason
   * if present. Undefined in the admin app (decisions 2 & 3, 2026-07-07): amendments and
   * quick-auth are provider-only, so admins get neither the Amend button nor the
   * quick-auth summary — EncounterView treats a missing onEdit as fully read-only.
   */
  onEdit?: (amendmentReason?: string) => void
  /** Pass to EncounterForm's onSuccess — updates the selection without ending the editing session. */
  onSaveSuccess: (selection: EncounterSelection) => void
  /** Pending amendment reason to plumb into EncounterForm (§1.6.1). */
  amendmentReason?: string
  onAmendmentReasonConsumed: () => void
}

// Single owner of "which encounter is open, and on which surface" for the three
// EncounterView/EncounterForm mount points. Status comes from the live patient
// encounters query (useProfile().encounters), falling back to the status captured
// on the selection when it was created.
// §7.1 (work item 7): the "full note opened" audit event is logged here — one place
// all three mounts route through. The loggable condition lives in the pure
// getShouldLogEncounterView predicate; the effect below fires it once per
// (examId, open-session) and is fire-and-forget (never blocks or breaks note opening).
export const useEncounterOpenState = (): EncounterOpenState => {
  const { appName } = useApp()
  const { encounters } = useProfile()
  const { dataArr: encountersArr } = encounters

  const [selection, setSelection] = useState<EncounterSelection | null>(null)
  const [mode, setMode] = useState<EncounterOpenMode | null>(null)
  const [amendmentReason, setAmendmentReason] = useState<string | null>(null)

  const encountersRef = useRef(encountersArr)
  useEffect(() => {
    encountersRef.current = encountersArr
  }, [encountersArr])

  const liveEncounter = useMemo(
    () =>
      selection?.examId ? (encountersArr?.find(e => e.id === selection.examId) ?? null) : null,
    [encountersArr, selection?.examId],
  )
  const status = liveEncounter?.status ?? selection?.status

  // §1.6.1: detect that an amendment was re-authenticated (previousVersions grew)
  // and end the amendment session, returning the provider to the read-only view.
  const liveVersionsCount = liveEncounter?.authentication?.previousVersions?.length ?? 0
  const liveVersionsRef = useRef(liveVersionsCount)
  useEffect(() => {
    liveVersionsRef.current = liveVersionsCount
  }, [liveVersionsCount])
  const amendBaselineRef = useRef(0)
  useEffect(() => {
    if (mode === 'amending' && liveVersionsCount > amendBaselineRef.current) {
      setMode(null)
    }
  }, [mode, liveVersionsCount])

  // §7.1: the examId already logged for the current open-session. Reset in open()/on
  // close so re-renders, status flips (COMPLETED→AUTHENTICATED after authenticate), and
  // surface transitions (quick-auth → tap-through to the form) within one open don't
  // double-log — but reopening a note is a new session and logs again.
  const loggedViewExamIdRef = useRef<string | null>(null)

  const open = useCallback((sel: EncounterSelection | null) => {
    loggedViewExamIdRef.current = null
    if (!sel) {
      setSelection(null)
      setMode(null)
      setAmendmentReason(null)
      return
    }
    const knownStatus =
      sel.status ??
      (sel.examId ? encountersRef.current?.find(e => e.id === sel.examId)?.status : undefined)
    setMode(getInitialOpenMode(sel.examId, knownStatus))
    setAmendmentReason(null)
    setSelection(sel)
  }, [])

  const onEdit = useCallback((reason?: string) => {
    if (reason) {
      setAmendmentReason(reason)
      amendBaselineRef.current = liveVersionsRef.current
      setMode('amending')
    } else {
      setMode('editing')
    }
  }, [])

  // Fires on every form save (not just the first) — the selection is refreshed with
  // the server-returned exam id while the editing session (mode) is preserved, so a
  // Draft → Saved transition does not bounce the provider out of the form.
  const onSaveSuccess = useCallback((sel: EncounterSelection) => setSelection(sel), [])

  const onAmendmentReasonConsumed = useCallback(() => setAmendmentReason(null), [])

  const surface = useMemo(
    () =>
      selection ? getEncounterSurface({ appName, examId: selection.examId, status, mode }) : null,
    [selection, appName, status, mode],
  )

  // §1.6.1 / decisions 2 & 3 (2026-07-07): the admin app never exposes onEdit — admins
  // cannot initiate amendments and never see the quick-auth summary. Gated here (the
  // single owner of open-state for all three mounts) rather than per-mount.
  const canEdit = getCanEditEncounter(appName)
  const gatedOnEdit = canEdit ? onEdit : undefined

  // §7.1 audit logging of full-note opens. hasOnEdit mirrors what EncounterView receives
  // (gatedOnEdit is truthy exactly when canEdit), so the quick-auth-summary exclusion in
  // getShouldLogEncounterView matches the branch EncounterView actually renders.
  // Fire-and-forget (PM decision): §7.1 is served by the server-side log being reliable
  // when reachable — a logging RTT must never gate chart access, so failures only warn.
  const examId = selection?.examId ?? null
  useEffect(() => {
    if (!examId) return
    if (!getShouldLogEncounterView({ examId, surface, status, hasOnEdit: canEdit })) return
    if (loggedViewExamIdRef.current === examId) return
    loggedViewExamIdRef.current = examId
    logEncounterView({ encounterId: examId, appName }).catch(err =>
      console.warn('Failed to log encounter view (§7.1)', err),
    )
  }, [examId, surface, status, canEdit, appName])

  return useMemo(
    () => ({
      selection,
      surface,
      open,
      onEdit: gatedOnEdit,
      onSaveSuccess,
      amendmentReason: amendmentReason ?? undefined,
      onAmendmentReasonConsumed,
    }),
    [
      selection,
      surface,
      open,
      gatedOnEdit,
      onSaveSuccess,
      amendmentReason,
      onAmendmentReasonConsumed,
    ],
  )
}
